// @ts-nocheck
/**
 * Remark Statuses list — redesigned to obd3-gui pattern (FillPage +
 * PageHeader + DataTable + tinted row-action buttons + ConfirmDialog).
 * Preserved: multi/single delete, ACL gates via MODULE_ACL.REMARKS_STATUS,
 * search by remark name, CSV export.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_REMARK_STATUS_LIST,
  API_REMARK_STATUS_DELETE,
} from '@/service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const RemarkStatusList = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [remarkStatusList, setRemarkStatusList] = useState<any[]>([]);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
    fetchList();
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchList = () => {
    getApiCall(API_REMARK_STATUS_LIST)
      .then((resp: any) => {
        const list = (resp || []).map((val: any, idx: number) => ({
          ...val,
          key: idx + 1,
        }));
        setRemarkStatusList(list);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  };

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCheckboxToggle = (id: any) => {
    setSelectedRemarks((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRemarks.length === remarkStatusList.length) setSelectedRemarks([]);
    else setSelectedRemarks(remarkStatusList.map((r: any) => r.id));
  };

  const requestSingleDelete = (row: any) => {
    checkAuth(setToastProps, setShowToast);
    setPendingDelete(row);
  };

  const confirmDelete = async () => {
    const ids = pendingBulk
      ? selectedRemarks
      : pendingDelete
      ? [pendingDelete.id]
      : [];
    if (!ids.length) return;
    setDeleting(true);
    try {
      await postApiCall(API_REMARK_STATUS_DELETE, { remarkStatusIds: ids });
      toast.success('Remark status deleted successfully');
      if (pendingBulk) setSelectedRemarks([]);
      setPendingDelete(null);
      setPendingBulk(false);
      setLoading(true);
      fetchList();
    } catch (err: any) {
      if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
        errorCheck(err, navigate, setToastProps, setShowToast);
      } else {
        toast.error(friendlyApiError(err) || 'Failed to delete remark status');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'remark-status-list',
        data: remarkStatusList,
        columns: [
          { header: 'Remark', accessor: 'remark' },
          { header: 'Status', accessor: 'status' },
          {
            header: 'Created Date',
            accessor: (row: any) => formatDate(row.created_at),
          },
          {
            header: 'Created By',
            accessor: (row: any) => row.created_by_user?.username || '-',
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const remarksRole =
    isSuperAdmin || getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead =
    remarksRole &&
    hasPermission(
      moduleOperationsForReadPermissionCheck,
      MODULE_ACL.REMARKS_STATUS,
      'read',
    );
  const canEdit =
    remarksRole && !!loggedInUserACL?.[MODULE_ACL.REMARKS_STATUS]?.edit;
  const canDelete =
    remarksRole && !!loggedInUserACL?.[MODULE_ACL.REMARKS_STATUS]?.delete;
  const canAdd =
    remarksRole && !!loggedInUserACL?.[MODULE_ACL.REMARKS_STATUS]?.add;
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: '_select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={
              selectedRemarks.length === remarkStatusList.length &&
              remarkStatusList.length > 0
            }
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedRemarks.includes(row.original.id)}
            onCheckedChange={() => handleCheckboxToggle(row.original.id)}
            aria-label="Select row"
          />
        ),
      },
      {
        accessorKey: 'remark',
        header: 'Remark',
        cell: ({ row }) =>
          canEdit ? (
            <Link
              to={`/${ROUTES.REMARKS.EDIT.replace(':id', row.original.id)}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.remark}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{row.original.remark}</span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ row }) => {
          const raw = String(row.original.status || '').toLowerCase();
          const isPositive =
            raw === 'active' || raw === 'success' || raw === 'closed_won';
          const isNegative =
            raw === 'inactive' || raw === 'failed' || raw === 'closed_lost';
          const tone = isPositive
            ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
            : isNegative
              ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
              : 'bg-muted/60 text-muted-foreground ring-border/60';
          return (
            <span
              className={
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                tone
              }
            >
              {row.original.status}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorFn: (row: any) => row.created_by_user?.username,
        id: 'created_by',
        header: 'Created By',
        cell: ({ row }) => (
          <span className="text-sm text-foreground">
            {row.original.created_by_user?.username || '—'}
          </span>
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/${ROUTES.REMARKS.EDIT.replace(':id', row.original.id)}`,
                  );
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {selectedRemarks.length === 0 && canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  requestSingleDelete(row.original);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }
    return cols;
  }, [
    selectedRemarks,
    remarkStatusList,
    canEdit,
    canDelete,
    showActions,
    navigate,
  ]);

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Remark Statuses" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Remark Statuses"
        description="Master list of remark tags agents pick from during dispositions."
        actions={
          <>
            {canDelete && selectedRemarks.length > 0 && (
              <Button variant="destructive" onClick={() => setPendingBulk(true)}>
                <Trash2 className="h-4 w-4" />
                Delete Selected ({selectedRemarks.length})
              </Button>
            )}
            {remarkStatusList.length > 0 && (
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.REMARKS.ADD}`)}>
                <Plus className="h-4 w-4" />
                Create Remark Status
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="remark-status.list:v1"
        columns={columns}
        data={remarkStatusList}
        isLoading={loading}
        searchPlaceholder="Search by remark…"
        emptyTitle="No remark statuses"
        emptyDescription="No remark statuses match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null || pendingBulk}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDelete(null);
            setPendingBulk(false);
          }
        }}
        title={
          pendingBulk
            ? 'Delete selected remark statuses?'
            : 'Delete this remark status?'
        }
        description={
          pendingBulk ? (
            <>
              <span className="font-medium text-foreground">
                {selectedRemarks.length}
              </span>{' '}
              remark status(es) will be permanently deleted.
            </>
          ) : (
            <>
              Remark status{' '}
              <span className="font-medium text-foreground">
                {pendingDelete?.remark}
              </span>{' '}
              will be permanently deleted.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default RemarkStatusList;
